import { Link } from 'react-router-dom';

export interface ServiceItem {
  icon: string;
  title: string;
  description: string;
  href: string;
  image?: string;
}

export interface ServicesOverviewProps {
  title: string;
  subtitle: string;
  services: ServiceItem[];
  ctaLabel?: string;
}

export default function ServicesOverview({ title, subtitle, services, ctaLabel = 'Learn More' }: ServicesOverviewProps) {
  const [featured, ...rest] = services;

  return (
    <section className="bg-ivory py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-bronze text-xs font-medium uppercase tracking-widest mb-3">What We Offer</p>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-charcoal mb-4">{title}</h2>
          <p className="text-gray-500 max-w-xl mx-auto">{subtitle}</p>
          <div className="w-16 h-0.5 bg-bronze mx-auto mt-6"></div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Featured card */}
          {featured && (
            <div className="lg:col-span-2 relative rounded-2xl overflow-hidden min-h-80 group cursor-pointer">
              {featured.image ? (
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
              ) : (
                <div className="absolute inset-0 bg-charcoal"></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/40 to-transparent"></div>
              <div className="relative z-10 h-full flex flex-col justify-end p-8">
                <div className="w-12 h-12 flex items-center justify-center bg-bronze rounded-full mb-4">
                  <i className={`${featured.icon} text-white text-xl`}></i>
                </div>
                <h3 className="font-serif text-2xl font-semibold text-white mb-2">{featured.title}</h3>
                <p className="text-white/70 text-sm mb-5 leading-relaxed">{featured.description}</p>
                <Link
                  to={featured.href}
                  className="inline-flex items-center gap-2 text-white text-sm font-medium hover:gap-3 transition-all cursor-pointer"
                >
                  {ctaLabel}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              </div>
            </div>
          )}

          {/* Grid cards */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {rest.map((service) => (
              <div
                key={service.title}
                className="bg-white rounded-xl p-6 border border-warmgray hover:-translate-y-1 transition-transform duration-300 cursor-pointer group"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-bronze/10 rounded-lg mb-4">
                  <i className={`${service.icon} text-bronze text-xl`}></i>
                </div>
                <h3 className="font-serif text-lg font-semibold text-charcoal mb-2">{service.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{service.description}</p>
                <Link
                  to={service.href}
                  className="inline-flex items-center gap-1.5 text-bronze text-sm font-medium group-hover:gap-2.5 transition-all cursor-pointer"
                >
                  {ctaLabel}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Mid-page CTA */}
        <div className="text-center mt-12">
          <Link
            to="/#quote"
            className="inline-flex items-center gap-2 bg-ivory border-2 border-bronze text-bronze font-medium px-8 py-3.5 rounded-md hover:bg-bronze hover:text-white transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Get a Free Estimate for Any Service
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
